'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';

interface HeritageTimelineProps {
  className?: string;
}

interface TimelineItem {
  id: number;
  eraKey: string;
  yearKey: string;
  titleKey: string;
  descriptionKey: string;
  image: string;
  animateFrom: 'left' | 'right';
}

const HeritageTimeline = ({ className = '' }: HeritageTimelineProps) => {
  const { t } = useTranslation();
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const timelineData: TimelineItem[] = [
    {
      id: 1,
      eraKey: 'ancientEgyptEra',
      yearKey: 'ancientEgyptYear',
      titleKey: 'birthOfFineTextiles',
      descriptionKey: 'birthOfFineTextilesDesc',
      image: '/images/heritage/pharaonic-weaving.jpg',
      animateFrom: 'left',
    },
    {
      id: 2,
      eraKey: 'traditionalCraftsmanshipEra',
      yearKey: 'traditionalCraftsmanshipYear',
      titleKey: 'generationsOfMastery',
      descriptionKey: 'generationsOfMasteryDesc',
      image: '/images/heritage/traditional-craftsman.jpg',
      animateFrom: 'right',
    },
    {
      id: 3,
      eraKey: 'modernEra',
      yearKey: 'modernEraYear',
      titleKey: 'globalExcellenceManufacturing',
      descriptionKey: 'globalExcellenceManufacturingDesc',
      image: '/images/heritage/modern-factory.jpg',
      animateFrom: 'left',
    },
  ];

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    itemRefs.current.forEach((ref, index) => {
      if (!ref) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleItems((prev) => new Set([...prev, index]));
          }
        },
        {
          threshold: 0.2,
          rootMargin: '-50px',
        }
      );

      observer.observe(ref);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  return (
    <section className={`py-24 bg-gradient-to-b from-stone-900 via-stone-800 to-stone-900 ${className}`}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="inline-block px-6 py-2 bg-amber-100/10 backdrop-blur-sm border border-amber-200/30 text-amber-200 rounded-full text-sm font-semibold tracking-widest uppercase mb-6">
            {t('ourHeritage')}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif">
            {t('legacyWovenThroughTime')}
          </h2>
          <p className="text-xl text-stone-300 max-w-3xl mx-auto">
            {t('heritageTimelineDesc')}
          </p>
        </div>

        {/* Timeline Items */}
        <div className="relative">
          {/* Vertical Timeline Line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-600 via-amber-500 to-amber-600 transform -translate-x-1/2 hidden lg:block" />

          {timelineData.map((item, index) => {
            const isVisible = visibleItems.has(index);
            const isEven = index % 2 === 0;

            return (
              <div
                key={item.id}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                className="relative mb-24 last:mb-0"
              >
                {/* Desktop Layout (side by side) */}
                <div className="hidden lg:grid lg:grid-cols-2 gap-12 items-center">
                  {/* Left Side */}
                  <div
                    className={`${isEven ? 'order-1' : 'order-2'
                      } transition-all duration-1000 ${isVisible
                        ? 'opacity-100 translate-x-0'
                        : `opacity-0 ${isEven ? '-translate-x-20' : 'translate-x-20'}`
                      }`}
                  >
                    {isEven ? (
                      <TimelineContent item={item} align="right" />
                    ) : (
                      <TimelineImage image={item.image} title={t(item.titleKey as any)} />
                    )}
                  </div>

                  {/* Center Marker */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                    <div className="relative">
                      <div className="w-16 h-16 bg-amber-600 rounded-full border-4 border-stone-900 flex items-center justify-center shadow-2xl">
                        <span className="text-white font-bold text-sm">{item.id}</span>
                      </div>
                      {/* Year Badge */}
                      <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 whitespace-nowrap px-4 py-1 bg-stone-800 border border-amber-600/50 rounded-full text-amber-200 text-xs font-bold">
                        {t(item.yearKey as any)}
                      </div>
                    </div>
                  </div>

                  {/* Right Side */}
                  <div
                    className={`${isEven ? 'order-2' : 'order-1'
                      } transition-all duration-1000 ${isVisible
                        ? 'opacity-100 translate-x-0'
                        : `opacity-0 ${isEven ? 'translate-x-20' : '-translate-x-20'}`
                      }`}
                  >
                    {isEven ? (
                      <TimelineImage image={item.image} title={t(item.titleKey as any)} />
                    ) : (
                      <TimelineContent item={item} align="left" />
                    )}
                  </div>
                </div>

                {/* Mobile Layout (stacked) */}
                <div
                  className={`lg:hidden transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                >
                  <TimelineImage image={item.image} title={t(item.titleKey as any)} />
                  <div className="mt-6">
                    <TimelineContent item={item} align="left" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Timeline Content Component
const TimelineContent = ({
  item,
  align,
}: {
  item: TimelineItem;
  align: 'left' | 'right';
}) => {
  const { t } = useTranslation();

  return (
    <div className={`${align === 'right' ? 'text-right' : 'text-left'}`}>
      <div className="inline-block px-4 py-1 bg-amber-600/20 border border-amber-600/40 rounded-full text-amber-300 text-xs font-semibold tracking-wider uppercase mb-4">
        {t(item.eraKey as any)}
      </div>
      <h3 className="text-3xl font-bold text-white mb-4 font-serif">{t(item.titleKey as any)}</h3>
      <p className="text-lg text-stone-300 leading-relaxed">{t(item.descriptionKey as any)}</p>
    </div>
  );
};

// Timeline Image Component
const TimelineImage = ({ image, title }: { image: string; title: string }) => {
  return (
    <div className="relative group overflow-hidden rounded-2xl shadow-2xl">
      <div
        className="w-full h-80 bg-cover bg-center transform group-hover:scale-110 transition-transform duration-700"
        style={{ backgroundImage: `url(${image})` }}
      />
      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />

      {/* Border Accent */}
      <div className="absolute inset-0 border-2 border-amber-600/30 rounded-2xl" />
    </div>
  );
};

export default HeritageTimeline;
